<script setup>
import { useI18n } from 'vue-i18n';

defineProps({
  previewHeader: { type: String, default: '' },
  previewBody: { type: String, default: '' },
  previewFooter: { type: String, default: '' },
  headerType: { type: String, default: 'NONE' },
  headerMediaPreviewUrl: { type: String, default: '' },
  headerMediaFile: { type: Object, default: null },
  headerFileName: { type: String, default: '' },
  quickReplies: { type: Array, default: () => [] },
  ctaButtons: { type: Array, default: () => [] },
});

const { t } = useI18n();
</script>

<template>
  <!-- Outer wrap — matches concept-a .phone-wrap -->
  <div class="flex flex-col items-center gap-2">
    <!-- LIVE PREVIEW label -->
    <p
      class="text-[11px] font-semibold text-n-slate-9 uppercase tracking-[.06em] mb-2"
    >
      {{ t('INBOX_MGMT.TEMPLATES.CREATE.PREVIEW') }}
    </p>

    <!-- Phone shell — 240px wide, rounded-3xl, thick border, WhatsApp wallpaper bg -->
    <div
      class="w-[240px] min-h-[380px] rounded-3xl border-2 border-n-slate-7 overflow-hidden shadow-2xl bg-[#e5ddd5] dark:bg-[#1a1f28] flex flex-col"
    >
      <!-- WhatsApp top bar -->
      <div class="bg-[#128c7e] h-[46px] flex items-center px-3 gap-2 shrink-0">
        <!-- Avatar circle -->
        <div
          class="w-7 h-7 rounded-full bg-white/30 flex items-center justify-center shrink-0"
        >
          <span class="text-white text-[11px] font-semibold leading-none">
            {{ t('INBOX_MGMT.TEMPLATES.CREATE.PREVIEW_AVATAR') }}
          </span>
        </div>
        <!-- Name + status -->
        <div class="min-w-0">
          <p
            class="text-white text-[13px] font-semibold leading-tight truncate"
          >
            {{ t('INBOX_MGMT.TEMPLATES.CREATE.PREVIEW_CONTACT') }}
          </p>
          <p class="text-white/70 text-[10px] leading-tight">
            {{ t('INBOX_MGMT.TEMPLATES.CREATE.PREVIEW_ONLINE') }}
          </p>
        </div>
        <!-- Right icons (decorative) -->
        <div class="ml-auto flex items-center gap-3">
          <svg
            class="w-4 h-4 text-white/80"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8l8 5 8-5v10zm-8-7L4 6h16l-8 5z"
            />
          </svg>
        </div>
      </div>

      <!-- Chat body — wallpaper area -->
      <div class="flex-1 p-2.5 flex flex-col gap-1.5 min-h-[320px]">
        <!-- Message bubble — left-anchored, tl corner flat (WhatsApp style) -->
        <div
          class="max-w-[210px] bg-white dark:bg-[#2a2f3a] rounded-[8px] rounded-tl-[2px] shadow overflow-hidden"
        >
          <!-- Media header area -->
          <template v-if="headerType !== 'NONE'">
            <!-- Actual image upload preview -->
            <img
              v-if="headerType === 'IMAGE' && headerMediaPreviewUrl"
              :src="headerMediaPreviewUrl"
              class="w-full max-h-[100px] object-cover"
              alt="header"
            />
            <!-- Image placeholder -->
            <div
              v-else-if="headerType === 'IMAGE'"
              class="w-full h-[100px] flex items-center justify-center bg-[linear-gradient(135deg,#a8d8c8_0%,#7ec8b0_100%)]"
            >
              <svg
                class="w-7 h-7 text-white/80"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <!-- Video preview -->
            <video
              v-else-if="headerType === 'VIDEO' && headerMediaPreviewUrl"
              :src="headerMediaPreviewUrl"
              controls
              class="w-full max-h-[100px]"
            />
            <!-- Video placeholder -->
            <div
              v-else-if="headerType === 'VIDEO'"
              class="w-full h-[100px] flex items-center justify-center bg-[linear-gradient(135deg,#3a86ff_0%,#5e60ce_100%)]"
            >
              <svg
                class="w-7 h-7 text-white/80"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <!-- Document with file name -->
            <div
              v-else-if="headerType === 'DOCUMENT' && headerMediaFile"
              class="flex items-center gap-2 px-3 py-2 bg-n-slate-2"
            >
              <svg
                class="w-5 h-5 text-n-slate-9 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <span class="text-[11px] text-n-slate-11 truncate">{{
                headerFileName
              }}</span>
            </div>
            <!-- Document placeholder -->
            <div
              v-else-if="headerType === 'DOCUMENT'"
              class="w-full h-16 flex items-center justify-center bg-[linear-gradient(135deg,#f72585_0%,#b5179e_100%)]"
            >
              <svg
                class="w-6 h-6 text-white/80"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
          </template>

          <!-- Message text area -->
          <div class="px-[10px] pt-2 pb-[6px]">
            <!-- Text header (bold) -->
            <p
              v-if="headerType === 'TEXT' && previewHeader"
              class="text-[12.5px] font-semibold text-[#1c2243] dark:text-n-slate-12 mb-1 leading-snug"
            >
              {{ previewHeader }}
            </p>

            <!-- Body -->
            <p
              class="text-[12.5px] text-[#1c2243] dark:text-n-slate-12 whitespace-pre-line leading-[1.45] break-words"
            >
              {{
                previewBody || t('INBOX_MGMT.TEMPLATES.CREATE.BODY_PREVIEW_PH')
              }}
            </p>

            <!-- Footer -->
            <p
              v-if="previewFooter"
              class="text-[11px] text-[#6b7a8d] dark:text-n-slate-9 mt-1"
            >
              {{ previewFooter }}
            </p>

            <!-- Timestamp + read-ticks -->
            <p class="text-[10px] text-[#8c9aa8] text-right mt-1 pb-[2px]">
              {{ t('INBOX_MGMT.TEMPLATES.CREATE.PREVIEW_TIMESTAMP') }}
            </p>
          </div>

          <!-- Buttons divider + rows -->
          <template v-if="quickReplies.length || ctaButtons.length">
            <div class="h-px bg-[#e0e0e0] dark:bg-white/10" />

            <!-- Quick replies -->
            <div
              v-for="(qr, i) in quickReplies"
              :key="`pqr-${i}`"
              class="flex items-center justify-center gap-2 px-[10px] py-2 border-b border-[#e0e0e0] dark:border-white/10 last:border-b-0"
            >
              <svg
                class="w-3.5 h-3.5 text-[#128c7e] shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                />
              </svg>
              <span class="text-[12px] font-medium text-[#128c7e]">{{
                qr || t('INBOX_MGMT.TEMPLATES.CREATE.QUICK_REPLY_PH')
              }}</span>
            </div>

            <!-- CTA buttons -->
            <div
              v-for="(btn, i) in ctaButtons"
              :key="`pcta-${i}`"
              class="flex items-center justify-center gap-2 px-[10px] py-2 border-b border-[#e0e0e0] dark:border-white/10 last:border-b-0"
            >
              <svg
                v-if="btn.type === 'URL'"
                class="w-3.5 h-3.5 text-[#128c7e] shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
              <svg
                v-else-if="btn.type === 'PHONE_NUMBER'"
                class="w-3.5 h-3.5 text-[#128c7e] shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              <span class="text-[12px] font-medium text-[#128c7e]">
                {{
                  btn.label ||
                  (btn.type === 'URL'
                    ? t('INBOX_MGMT.TEMPLATES.CREATE.OPEN_URL')
                    : t('INBOX_MGMT.TEMPLATES.CREATE.CALL_PHONE'))
                }}
              </span>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- End-to-end encrypted caption -->
    <p class="text-[11px] text-n-slate-9 mt-1 flex items-center gap-1">
      <svg
        class="w-3 h-3"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
        />
      </svg>
      {{ t('INBOX_MGMT.TEMPLATES.CREATE.PREVIEW_ENCRYPTED') }}
    </p>
  </div>
</template>
